import { HStack, VStack, useTheme, IconButton, ScrollView, FlatList, Box, useBreakpointValue } from 'native-base';
import { groupBy, map, sortBy } from 'lodash';

import { CloseIcon, Text } from '@pimm/base';
import { useMemo } from 'react';
import { useAppLocale } from '@pimm/common';
import { t } from 'i18next';
import { useWidgetModules } from '../context/widget-modules.context';

type WidgetCenterProps = {
  _container?: React.ComponentProps<typeof VStack>;
  onClose: () => void;
};

export const WidgetCenter = ({ _container, onClose }: WidgetCenterProps) => {
  const { colors } = useTheme();
  const { translate, locale } = useAppLocale();
  const { widgets } = useWidgetModules();

  const widgetList = useMemo(() => {
    const groupBySections = groupBy(widgets, w => w.sectionTitle ?? (w.payload as { sectionTitle?: string })?.sectionTitle ?? '');

    const sections = map(groupBySections, (items, sectionTitle) => {
      const first = items[0];
      return {
        title: sectionTitle || undefined,
        titleTranslations: first?.sectionTitleTranslations ?? first?.titleTranslations,
        seq: first?.sectionSeq,
        widgets: items,
      };
    });

    return sortBy(sections, section => section.seq ?? Number.MAX_SAFE_INTEGER);
  }, [widgets]);

  return (
    <VStack rounded="2xl" flex={1} bgColor="gray.200" pb={2} {..._container}>
      <HStack py={2} px={4} justifyContent="space-between" alignItems="center" bgColor="white" roundedTop="xl">
        <Text size="xl" color="black" fontWeight={700}>
          {`${t('widgets:widget_center')}`}
        </Text>
        <IconButton rounded="sm" onPress={onClose} icon={<CloseIcon size={4} color={colors.gray[700]} />} />
      </HStack>

      <ScrollView mt={2} flex={1}>
        <VStack w="full" space={2} px={3}>
          {map(widgetList, section => {
            return (
              <VStack w="full" key={section.title} space={1} p={1.5}>
                <Text size="xl" fontWeight={700} color="black">
                  {translate(section.title, section.titleTranslations)}
                </Text>
                <HStack w="full" flexWrap="wrap" space={2}>
                  {map(section.widgets, (module, i) => {
                    if (!module.component) return null;
                    const Widget = module.component as React.ComponentType<any>;
                    return (
                      <Box key={`widget_center${module.identifier}.${i}`} h={190} minW={350}>
                        <Widget title={module?.title} titleTranslations={module?.titleTranslations} flex={1} />
                      </Box>
                    );
                  })}
                </HStack>
              </VStack>
            );
          })}
        </VStack>
      </ScrollView>
    </VStack>
  );
};
